import axios from "axios";

// In production (same host serves UI + API): `/api` keeps requests same-origin.
// In development: CRA's package.json proxy often fails for POST from LAN IPs ("Cannot POST /api/login").
// So we call the API directly on port 5041 using the same hostname as the page (localhost or 192.168.x.x).
// Override with REACT_APP_API_URL when needed.
const resolveApiBaseUrl = () => {
  const raw = process.env.REACT_APP_API_URL;
  if (raw != null && String(raw).trim()) return String(raw).trim().replace(/\/+$/, "");

  if (
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.location?.hostname
  ) {
    return `http://${window.location.hostname}:5041/api`;
  }

  return "/api";
};

const API_BASE_URL = resolveApiBaseUrl();

// Upload URL base should NOT include `/api`.
// In many deployments REACT_APP_API_URL is set to ".../api", which would incorrectly
// produce URLs like "http://host/api/uploads/...".
const deriveUploadBaseUrl = () => {
  const explicit = process.env.REACT_APP_UPLOAD_URL;
  if (explicit && String(explicit).trim()) return String(explicit).replace(/\/+$/, "");

  const envApi = process.env.REACT_APP_API_URL;
  if (envApi != null && String(envApi).trim()) {
    const apiUrl = String(envApi).trim();
    return apiUrl.replace(/\/api\/?$/i, "").replace(/\/+$/, "");
  }

  if (
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    window.location?.hostname
  ) {
    return `http://${window.location.hostname}:5041`;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }

  return "http://localhost:5041";
};

const UPLOAD_BASE_URL = deriveUploadBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const getUploadUrl = (relativePath) => {
  if (!relativePath || typeof relativePath !== "string") return null;
  const raw = relativePath.trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  const trimmed = relativePath.replace(/^\/+/, "");
  return `${UPLOAD_BASE_URL}/uploads/${trimmed}`;
};

// Get user ID from localStorage
const getUserId = () => {
  const user = localStorage.getItem("user");
  return user ? JSON.parse(user).id : null;
};

// Add user_id to requests
api.interceptors.request.use((config) => {
  const userId = getUserId();
  if (userId) {
    // Add user_id to headers for all requests
    config.headers["x-user-id"] = userId;
    // Also add to params for GET requests and data for POST/PUT/DELETE
    if (config.method === "get" || config.method === "delete") {
      config.params = { ...config.params, user_id: userId };
    } else if (config.data instanceof FormData) {
      // For FormData, append user_id
      config.data.append("user_id", userId);
      // Let axios set the correct multipart boundary
      if (config.headers && config.headers["Content-Type"]) {
        delete config.headers["Content-Type"];
      }
    } else {
      config.data = { ...config.data, user_id: userId };
    }
  }
  return config;
});

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post("/login", { username, password });
    return response.data;
  },
};

export const userAPI = {
  listUsers: async () => {
    const response = await api.get("/users");
    return response.data;
  },
  createUser: async (username, password, role) => {
    const response = await api.post("/users", { username, password, role });
    return response.data;
  },
  updateUser: async (id, fields) => {
    const response = await api.put(`/users/${id}`, fields);
    return response.data;
  },
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

export const companyAPI = {
  listCompanies: async () => {
    const response = await api.get("/companies");
    return response.data;
  },
  createCompany: async ({
    name,
    slug,
    purchase_date,
    payment_cycle,
    contact_name,
    contact_email,
    contact_phone,
    device_limit,
    additional_info,
    admin,
    logo,
  }) => {
    const hasLogo = logo instanceof File;
    const payload = hasLogo ? new FormData() : {
      name,
      purchase_date,
      payment_cycle,
      device_limit,
      admin,
    };

    if (hasLogo) {
      payload.append("name", name);
      payload.append("purchase_date", purchase_date);
      payload.append("payment_cycle", payment_cycle);
      payload.append("device_limit", String(device_limit ?? 0));
      payload.append("slug", slug);
      if (contact_name !== undefined) payload.append("contact_name", contact_name);
      if (contact_email !== undefined) payload.append("contact_email", contact_email);
      if (contact_phone !== undefined) payload.append("contact_phone", contact_phone);
      if (additional_info !== undefined) payload.append("additional_info", additional_info);
      // Send admin as JSON string (backend also supports bracket fields)
      payload.append("admin", JSON.stringify(admin));
      payload.append("logo", logo);
    } else {
      payload.slug = slug;
      if (contact_name !== undefined) payload.contact_name = contact_name;
      if (contact_email !== undefined) payload.contact_email = contact_email;
      if (contact_phone !== undefined) payload.contact_phone = contact_phone;
      if (additional_info !== undefined) payload.additional_info = additional_info;
    }

    const response = await api.post("/companies", payload);
    return response.data;
  },
  updateCompany: async (id, fields) => {
    const logo = fields?.logo;
    const hasLogo = logo instanceof File;

    // Slug is immutable; never send it to backend.
    const safeFields = { ...(fields || {}) };
    delete safeFields.slug;
    const payload = hasLogo ? new FormData() : safeFields;

    if (hasLogo) {
      // Copy all scalar fields into FormData
      Object.entries(safeFields || {}).forEach(([key, value]) => {
        if (key === "logo") return;
        if (value === undefined) return;
        if (value === null) return payload.append(key, "");
        payload.append(key, String(value));
      });
      payload.append("logo", logo);
    }

    const response = await api.put(`/companies/${id}`, payload);
    return response.data;
  },
  deleteCompany: async (id) => {
    const response = await api.delete(`/companies/${id}`);
    return response.data;
  },
  getCompanyAdmin: async (id) => {
    const response = await api.get(`/companies/${id}/admin`);
    return response.data;
  },
  resetCompanyAdminPassword: async (id) => {
    const response = await api.post(`/companies/${id}/admin/reset-password`, {});
    return response.data;
  },
};

export const messageAPI = {
  getMessage: async () => {
    const response = await api.get("/message");
    return response.data;
  },
};

export const playlistAPI = {
  createPlaylist: async (name, description) => {
    const response = await api.post("/playlists", { name, description });
    return response.data;
  },

  getPlaylists: async () => {
    const response = await api.get("/playlists");
    return response.data;
  },

  getPlaylist: async (id) => {
    const response = await api.get(`/playlists/${id}`);
    return response.data;
  },

  updatePlaylist: async (id, name, description) => {
    const response = await api.put(`/playlists/${id}`, { name, description });
    return response.data;
  },

  deletePlaylist: async (id) => {
    const response = await api.delete(`/playlists/${id}`);
    return response.data;
  },

  uploadFile: async (playlistId, file, duration, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    if (duration) {
      formData.append("duration", duration);
    }
    const userId = getUserId();
    if (userId) {
      formData.append("user_id", userId);
    }

    const response = await axios.post(
      `${API_BASE_URL}/playlists/${playlistId}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-user-id": userId || "",
        },
        onUploadProgress: (evt) => {
          const total = evt?.total;
          const loaded = evt?.loaded ?? 0;
          if (!options?.onProgress) return;
          if (!total || total <= 0) return options.onProgress(0, evt);
          const percent = Math.max(0, Math.min(100, Math.round((loaded * 100) / total)));
          return options.onProgress(percent, evt);
        },
      }
    );
    return response.data;
  },

  // Upload multiple files one at a time (avoids proxy/request body limits on batched uploads).
  // files: File[]
  // durations: number[] | (number | null)[]  (per-file durations, same order as files; use null/undefined for videos)
  // options: { onProgress?: (percent:number, evt?:ProgressEvent) => void }
  uploadFiles: async (playlistId, files, durations = [], options = {}) => {
    const validFiles = files.filter(Boolean);
    if (!validFiles.length) {
      throw new Error("No files to upload");
    }

    const uploadedFiles = [];
    const playlistItems = [];

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const duration = durations[i];
      const fileDuration =
        duration != null && duration !== "" && !Number.isNaN(duration) ? duration : null;

      const result = await playlistAPI.uploadFile(playlistId, file, fileDuration, {
        onProgress: (filePercent) => {
          if (!options?.onProgress) return;
          const overall = Math.round(((i + filePercent / 100) / validFiles.length) * 100);
          options.onProgress(Math.max(0, Math.min(100, overall)));
        },
      });

      if (result?.file) uploadedFiles.push(result.file);
      if (result?.playlistItem) playlistItems.push(result.playlistItem);
    }

    return { success: true, files: uploadedFiles, playlistItems };
  },

  getPlaylistItems: async (playlistId) => {
    const response = await api.get(`/playlists/${playlistId}/items`);
    return response.data;
  },

  updateItemDuration: async (itemId, duration) => {
    const response = await api.put(`/playlist-items/${itemId}/duration`, { duration });
    return response.data;
  },

  updateItemOrder: async (itemId, direction) => {
    const response = await api.put(`/playlist-items/${itemId}/order`, { direction });
    return response.data;
  },

  deleteItem: async (itemId) => {
    const response = await api.delete(`/playlist-items/${itemId}`);
    return response.data;
  },

  setPlaylistActive: async (playlistId, deviceGroupId) => {
    const response = await api.post(`/playlists/${playlistId}/activate`, {
      device_group_id: deviceGroupId
    });
    return response.data;
  },

  setPlaylistInactive: async (playlistId) => {
    const response = await api.post(`/playlists/${playlistId}/deactivate`);
    return response.data;
  },

  schedulePlaylist: async (playlistId, startTime, endTime, deviceGroupId) => {
    const payload = {
      end_time: endTime,
      device_group_id: deviceGroupId,
    };
    if (startTime) payload.start_time = startTime;

    const response = await api.post(`/playlists/${playlistId}/schedule`, payload);
    return response.data;
  },

  clearOneTimeSchedule: async (playlistId) => {
    const response = await api.delete(`/playlists/${playlistId}/schedule`);
    return response.data;
  },

  createDailySchedule: async (playlistId, deviceGroupId, dailyStartTime, dailyEndTime, enabled = true) => {
    const response = await api.post(`/playlists/${playlistId}/schedules/daily`, {
      device_group_id: deviceGroupId,
      daily_start_time: dailyStartTime,
      daily_end_time: dailyEndTime,
      enabled,
    });
    return response.data;
  },

  listSchedules: async ({ deviceGroupId, playlistId } = {}) => {
    const params = {};
    if (deviceGroupId) params.device_group_id = deviceGroupId;
    if (playlistId) params.playlist_id = playlistId;
    const response = await api.get(`/schedules`, { params });
    return response.data;
  },

  updateSchedule: async (scheduleId, fields) => {
    const response = await api.put(`/schedules/${scheduleId}`, fields);
    return response.data;
  },

  deleteSchedule: async (scheduleId) => {
    const response = await api.delete(`/schedules/${scheduleId}`);
    return response.data;
  },
};

export const deviceAPI = {
  getDevices: async () => {
    const response = await api.get("/devices");
    return response.data;
  },

  getDevice: async (id) => {
    const response = await api.get(`/devices/${id}`);
    return response.data;
  },

  getDeviceStatus: async (id) => {
    const response = await api.get(`/devices/${id}/status`);
    return response.data;
  },

  createDevice: async (name, groupId) => {
    const response = await api.post("/devices", { name, groupId });
    return response.data;
  },

  deleteDevice: async (id) => {
    const response = await api.delete(`/devices/${id}`);
    return response.data;
  },

  assignPlaylist: async (deviceId, playlistId) => {
    const response = await api.put(`/devices/${deviceId}/playlist`, { playlistId });
    return response.data;
  },
};

export const deviceGroupAPI = {
  getGroups: async () => {
    const response = await api.get("/device-groups");
    return response.data;
  },

  getGroup: async (id) => {
    const response = await api.get(`/device-groups/${id}`);
    return response.data;
  },

  createGroup: async (name) => {
    const response = await api.post("/device-groups", { name });
    return response.data;
  },

  updateGroup: async (id, name) => {
    const response = await api.put(`/device-groups/${id}`, { name });
    return response.data;
  },

  deleteGroup: async (id) => {
    const response = await api.delete(`/device-groups/${id}`);
    return response.data;
  },

  updateGroupDevices: async (id, deviceIds) => {
    const response = await api.put(`/device-groups/${id}/devices`, { deviceIds });
    return response.data;
  },
};

export const dashboardAPI = {
  getSummary: async () => {
    const response = await api.get("/dashboard-summary");
    return response.data;
  },
};

export const getFileUrl = (filePath) => {
  if (!filePath || typeof filePath !== "string") return null;
  const raw = filePath.trim();
  if (/^https?:\/\//i.test(raw)) return raw;
  const trimmed = filePath.replace(/^\/+/, "");
  return `${UPLOAD_BASE_URL}/uploads/${trimmed}`;
};

export default api;

